import { inferGraphTimeZone, type GraphDocument, cryptoLikeId } from '../entities/graph-document';
import {
  createHumanNode,
  createOccupationNode,
  createProjectNode,
  currentTimeZone,
  type CustomFieldEntry,
  type HumanNode,
  type OccupationNode,
  type ProjectNode,
  type JourneyNode,
} from '../entities/node';
import type { AIResponseOperation, CreateNodeDraft, DeIdentificationRule } from '../entities/ai-assist-config';
import { canonicalizeNodeReferences } from '../policies/auto-connections/connection-input-normalizer';
import { deriveConnections } from '../policies/auto-connections/derive-connections';
import { restoreDeidentificationRules } from '../policies/anonymization/restore-deidentification-rules';
import { isPlainObject } from '../policies/anonymization/ai-node-field-validation';
import { hasInvalidTagValues } from '../policies/node-validation/tag-rules';
import { validateNode } from '../policies/node-validation/validate-node';
import { DEFAULT_CONNECTION_STRATEGY, type ConnectionStrategy } from '../ports/preferences-repository';

export interface ApplyAIOperationsIssue {
  operationIndex: number;
  message: string;
}

export interface ApplyAIOperationsResult {
  applied: boolean;
  graph: GraphDocument | null;
  issues: ApplyAIOperationsIssue[];
  createdNodeIds: string[];
  updatedNodeIds: string[];
}

export function normalizeCreateNodeDraftToOperation(payload: CreateNodeDraft): AIResponseOperation {
  const { nodeType, ...fields } = payload;
  return {
    action: 'create',
    nodeType,
    fields,
  };
}

export function applyAIOperations(
  graph: GraphDocument,
  operations: AIResponseOperation[],
  options: {
    owner: boolean;
    rules?: DeIdentificationRule[];
    connectionStrategy?: ConnectionStrategy;
  },
): ApplyAIOperationsResult {
  if (!options.owner) {
    return {
      applied: false,
      graph: null,
      issues: [{ operationIndex: -1, message: 'Read-only viewer cannot edit the owner graph.' }],
      createdNodeIds: [],
      updatedNodeIds: [],
    };
  }

  const rules = options.rules ?? [];
  const updatedAt = new Date().toISOString();
  const timeZone = currentTimeZone();
  const nextNodes: JourneyNode[] = graph.nodes.map((node) => ({ ...node }));
  const createdNodeIds: string[] = [];
  const updatedNodeIds: string[] = [];

  for (const [index, operation] of operations.entries()) {
    const restored = restoreValue(operation.fields, rules, `operations[${index}].fields`);
    if (restored.issues.length > 0) {
      return failed(index, restored.issues, createdNodeIds, updatedNodeIds);
    }
    if (!isPlainObject(restored.value)) {
      return failed(index, [`operations[${index}].fields must be an object.`], createdNodeIds, updatedNodeIds);
    }

    if (operation.action === 'create') {
      const nodeId = createImportedNodeId(operation.nodeType, nextNodes);
      const createdNode = buildCreatedNode(operation.nodeType, nodeId, restored.value, updatedAt, timeZone);
      const canonicalizedNode = canonicalizeAndValidateNode(createdNode, [...nextNodes, createdNode], index);
      if ('issues' in canonicalizedNode) {
        return failed(index, canonicalizedNode.issues, createdNodeIds, updatedNodeIds);
      }

      nextNodes.push(canonicalizedNode.node);
      createdNodeIds.push(canonicalizedNode.node.nodeId);
      continue;
    }

    const existingIndex = nextNodes.findIndex((node) => node.nodeId === operation.nodeId);
    if (existingIndex < 0) {
      return failed(index, [`operations[${index}].nodeId does not exist.`], createdNodeIds, updatedNodeIds);
    }

    const existingNode = nextNodes[existingIndex]!;
    if (existingNode.type !== operation.nodeType) {
      return failed(index, [`operations[${index}] targets ${existingNode.type}, not ${operation.nodeType}.`], createdNodeIds, updatedNodeIds);
    }

    const updatedNode = buildUpdatedNode(existingNode, restored.value, updatedAt, timeZone);
    const provisionalNodes = nextNodes.map((node, candidateIndex) => candidateIndex === existingIndex ? updatedNode : node);
    const canonicalizedNode = canonicalizeAndValidateNode(updatedNode, provisionalNodes, index, restored.value);
    if ('issues' in canonicalizedNode) {
      return failed(index, canonicalizedNode.issues, createdNodeIds, updatedNodeIds);
    }

    nextNodes[existingIndex] = canonicalizedNode.node;
    updatedNodeIds.push(canonicalizedNode.node.nodeId);
  }

  const nextGraph: GraphDocument = {
    ...graph,
    timestampOriginTimeZone: graph.timestampOriginTimeZone ?? inferGraphTimeZone({ ...graph, nodes: nextNodes }) ?? timeZone,
    createdAtTimeZone: graph.createdAtTimeZone ?? inferGraphTimeZone(graph) ?? timeZone,
    updatedAt,
    updatedAtTimeZone: timeZone,
    nodes: nextNodes,
    connections: deriveConnections(nextNodes, updatedAt, {
      strategy: options.connectionStrategy ?? DEFAULT_CONNECTION_STRATEGY,
      timeZone,
    }),
  };

  return {
    applied: true,
    graph: nextGraph,
    issues: [],
    createdNodeIds,
    updatedNodeIds,
  };
}

function failed(
  operationIndex: number,
  messages: string[],
  createdNodeIds: string[],
  updatedNodeIds: string[],
): ApplyAIOperationsResult {
  return {
    applied: false,
    graph: null,
    issues: messages.map((message) => ({ operationIndex, message })),
    createdNodeIds,
    updatedNodeIds,
  };
}

function restoreValue<T>(value: T, rules: DeIdentificationRule[], path: string): { value: T; issues: string[] } {
  if (typeof value === 'string') {
    const restored = restoreDeidentificationRules(value, rules);
    return {
      value: restored.text as T,
      issues: restored.issues.map((issue) => `${path}: ${issue}`),
    };
  }

  if (Array.isArray(value)) {
    const restoredItems = value.map((item, index) => restoreValue(item, rules, `${path}[${index}]`));
    return {
      value: restoredItems.map((item) => item.value) as T,
      issues: restoredItems.flatMap((item) => item.issues),
    };
  }

  if (isPlainObject(value)) {
    const restoredEntries = Object.entries(value).map(([key, entryValue]) => [key, restoreValue(entryValue, rules, `${path}.${key}`)] as const);
    return {
      value: Object.fromEntries(restoredEntries.map(([key, entryValue]) => [key, entryValue.value])) as T,
      issues: restoredEntries.flatMap(([, entryValue]) => entryValue.issues),
    };
  }

  return { value, issues: [] };
}

function buildCreatedNode(
  nodeType: AIResponseOperation['nodeType'],
  nodeId: string,
  fields: Record<string, unknown>,
  updatedAt: string,
  timeZone: string,
): JourneyNode {
  const commonFields = extractCommonFields(fields);

  if (nodeType === 'Human') {
    return createHumanNode({
      nodeId,
      ...commonFields,
      occupationIds: toStringArray(fields.occupationIds),
      projectIds: toStringArray(fields.projectIds),
      relatedHumanIds: toStringArray(fields.relatedHumanIds),
      createdAt: updatedAt,
      createdAtTimeZone: timeZone,
      updatedAt,
      updatedAtTimeZone: timeZone,
    });
  }

  if (nodeType === 'Project') {
    return createProjectNode({
      nodeId,
      ...commonFields,
      participantHumanIds: toStringArray(fields.participantHumanIds),
      relatedOccupationIds: toStringArray(fields.relatedOccupationIds),
      relatedProjectIds: toStringArray(fields.relatedProjectIds),
      createdAt: updatedAt,
      createdAtTimeZone: timeZone,
      updatedAt,
      updatedAtTimeZone: timeZone,
    });
  }

  return createOccupationNode({
    nodeId,
    ...commonFields,
    relatedHumanIds: toStringArray(fields.relatedHumanIds),
    typicalProjectIds: toStringArray(fields.typicalProjectIds),
    createdAt: updatedAt,
    createdAtTimeZone: timeZone,
    updatedAt,
    updatedAtTimeZone: timeZone,
  });
}

function buildUpdatedNode(
  existingNode: JourneyNode,
  fields: Record<string, unknown>,
  updatedAt: string,
  timeZone: string,
): JourneyNode {
  const commonPatch = extractCommonPatch(fields);

  if (existingNode.type === 'Human') {
    const updatedNode: HumanNode = {
      ...existingNode,
      ...commonPatch,
      ...(fields.occupationIds !== undefined ? { occupationIds: toStringArray(fields.occupationIds) } : {}),
      ...(fields.projectIds !== undefined ? { projectIds: toStringArray(fields.projectIds) } : {}),
      ...(fields.relatedHumanIds !== undefined ? { relatedHumanIds: toStringArray(fields.relatedHumanIds) } : {}),
      updatedAt,
      updatedAtTimeZone: timeZone,
    };

    return updatedNode;
  }

  if (existingNode.type === 'Project') {
    const updatedNode: ProjectNode = {
      ...existingNode,
      ...commonPatch,
      ...(fields.participantHumanIds !== undefined ? { participantHumanIds: toStringArray(fields.participantHumanIds) } : {}),
      ...(fields.relatedOccupationIds !== undefined ? { relatedOccupationIds: toStringArray(fields.relatedOccupationIds) } : {}),
      ...(fields.relatedProjectIds !== undefined ? { relatedProjectIds: toStringArray(fields.relatedProjectIds) } : {}),
      updatedAt,
      updatedAtTimeZone: timeZone,
    };

    return updatedNode;
  }

  const updatedNode: OccupationNode = {
    ...existingNode,
    ...commonPatch,
    ...(fields.relatedHumanIds !== undefined ? { relatedHumanIds: toStringArray(fields.relatedHumanIds) } : {}),
    ...(fields.typicalProjectIds !== undefined ? { typicalProjectIds: toStringArray(fields.typicalProjectIds) } : {}),
    updatedAt,
    updatedAtTimeZone: timeZone,
  };

  return updatedNode;
}

function canonicalizeAndValidateNode(
  node: JourneyNode,
  nodes: JourneyNode[],
  operationIndex: number,
  updatedFields?: Record<string, unknown>,
): { node: JourneyNode } | { issues: string[] } {
  const canonicalizedNode = canonicalizeNodeReferences(node, nodes);
  const validation = updatedFields
    ? validateUpdatedNodeFields(canonicalizedNode, updatedFields)
    : validateNode(canonicalizedNode);

  if (!validation.valid) {
    return {
      issues: validation.hints.map((hint) => `operations[${operationIndex}]: ${hint}`),
    };
  }

  return { node: canonicalizedNode };
}

function validateUpdatedNodeFields(node: JourneyNode, updatedFields: Record<string, unknown>): { valid: boolean; hints: string[] } {
  const hints: string[] = [];

  if (Object.prototype.hasOwnProperty.call(updatedFields, 'displayName') && !node.displayName.trim()) {
    hints.push('Display name is required.');
  }

  if (Object.prototype.hasOwnProperty.call(updatedFields, 'tags') && hasInvalidTagValues(node.tags)) {
    hints.push('Tags can only contain letters, numbers, and spaces. Use commas to separate multiple tags.');
  }

  if (Object.prototype.hasOwnProperty.call(updatedFields, 'customFields')) {
    const fieldIds = new Set<string>();
    for (const field of node.customFields) {
      if (!field.label.trim()) {
        hints.push('Custom field label is required.');
      }
      if (fieldIds.has(field.fieldId)) {
        hints.push('Custom field ids must be unique.');
      }
      fieldIds.add(field.fieldId);
    }
  }

  if (
    node.type === 'Project'
    && Object.prototype.hasOwnProperty.call(updatedFields, 'relatedProjectIds')
    && node.relatedProjectIds.includes(node.nodeId)
  ) {
    hints.push('Project cannot reference itself.');
  }

  return { valid: hints.length === 0, hints };
}

function extractCommonFields(fields: Record<string, unknown>): {
  displayName: string;
  summary?: string;
  tags?: string[];
  notes?: string;
  customFields?: CustomFieldEntry[];
} {
  return {
    displayName: String(fields.displayName ?? ''),
    ...(fields.summary !== undefined ? { summary: String(fields.summary) } : {}),
    ...(fields.tags !== undefined ? { tags: toStringArray(fields.tags) } : {}),
    ...(fields.notes !== undefined ? { notes: String(fields.notes) } : {}),
    ...(fields.customFields !== undefined ? { customFields: fields.customFields as CustomFieldEntry[] } : {}),
  };
}

function extractCommonPatch(fields: Record<string, unknown>): Partial<Pick<JourneyNode, 'displayName' | 'summary' | 'tags' | 'notes' | 'customFields'>> {
  return {
    ...(fields.displayName !== undefined ? { displayName: String(fields.displayName) } : {}),
    ...(fields.summary !== undefined ? { summary: String(fields.summary) } : {}),
    ...(fields.tags !== undefined ? { tags: toStringArray(fields.tags) } : {}),
    ...(fields.notes !== undefined ? { notes: String(fields.notes) } : {}),
    ...(fields.customFields !== undefined ? { customFields: fields.customFields as CustomFieldEntry[] } : {}),
  };
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map((entry) => String(entry)) : [];
}

function createImportedNodeId(nodeType: AIResponseOperation['nodeType'], nodes: JourneyNode[]): string {
  const prefix = nodeType.toLocaleLowerCase();
  const existingIds = new Set(nodes.map((node) => node.nodeId));
  let nextId = `${prefix}-${cryptoLikeId()}`;

  while (existingIds.has(nextId)) {
    nextId = `${prefix}-${cryptoLikeId()}`;
  }

  return nextId;
}